import { computed, ref } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from '@axios'
import moment from 'moment-timezone'
import store from '@/store'
import router from '@/router'

const LOCAL_DATE_VENDORS = ['dahua_camera', 'luxriot_camera', 'hanwha_camera', 'hikvision_camera']

const JOB_IDLE_TIMEOUT = 75 * 60 * 1000

const VENDOR_EXPORT_PARAMS = {
  camera_manager: { signed: true },
  hanwha_camera: { signed: true, videoConfig: { videoFormat: 'avi' } },
}

export default function useList() {

  const toast = useToast()

  const idRecording = router.currentRoute.params.id_recording

  const recordingOptions = ref(null)

  const downloading = ref(null)

  const cameras = computed(() => (recordingOptions.value?.cameras ?? [])
    .map(item => ({ ...item, camera: item.camera?.camera }))
    .filter(item => item.camera))


  const formatDateAssigned = value => {
    const formattedDate = moment(value).format('DD/MM/YYYY HH:mm:ss')
    return formattedDate
  }

  const fileNameFormat = fileName => fileName.replace(/\s+/g, '_').replace(/:/g, '_')

  const formatDate = (value, cameraType) => {
    if (LOCAL_DATE_VENDORS.includes(cameraType)) {
      return moment(value).format('YYYY-MM-DD HH:mm:ss')
    }

    return moment.tz(value, moment.tz.guess()).utc().format('YYYY-MM-DD HH:mm:ss')
  }

  const partialMessage = (done, total) => `Listo para descargar ${done} de ${total}`

  const getProgressMessage = (progress = 0) => {
    if (progress < 50) return 'Generando video...'
    if (progress < 90) return 'Guardando en el filesystem...'
    if (progress < 100) return 'Generando enlace de descarga...'
    return 'Listo para descargar'
  }

  const notificationTimestamp = () => {
    const now = new Date()
    return `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`
  }

  const setNotification = (notification, action = 'grid/updateDownloadNotification') => {
    store.dispatch(action, notification)
  }

  const createJob = camera => {
    const { camera_type, camera_id } = camera.camera

    return axios
      .get(`v1/services/download-video/${camera_type}/${camera_id}`, {
        params: {
          cameraId: camera_id,
          start: formatDate(camera.from, camera_type),
          end: formatDate(camera.to, camera_type),
          fileName: fileNameFormat(`export ${camera.camera.name} ${camera.from}`),
          ...(VENDOR_EXPORT_PARAMS[camera_type] ?? {}),
        },
      })
      .then(({ data }) => data.data.id[0])
  }

  const listenJob = (id, onProgress) => new Promise((resolve, reject) => {
    const channel = `video-download-${id}`

    let timer = null

    const close = () => {
      clearTimeout(timer)
      window.Echo.leave(channel)
    }

    const waitForActivity = () => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        close()
        reject(new Error('timeout'))
      }, JOB_IDLE_TIMEOUT)
    }

    waitForActivity()

    window.Echo.private(channel)
      .listen('.status', ({ message }) => {
        waitForActivity()
        onProgress(message.percent || 10)
      })
      .listen('.update', ({ message }) => {
        close()

        if (message.status === 'ok') {
          resolve(message.url)
          return
        }

        reject(new Error(message.msg))
      })
  })

  const isApproved = computed(
    () => recordingOptions.value?.last_validation?.status?.name === 'approved'
  )

  const storeJob = (items, urls) => axios
    .post(`v1/recording_request/${idRecording}/recordings`, {
      videos: items.map((camera, index) => ({ id: camera.id, url: urls[index] })),
    })
    .then(({ data }) => data.data.id[0])

  const downloadFile = (url, name) => {
    const link = document.createElement('a')

    link.href = url
    link.target = '_blank'
    document.body.appendChild(link)

    if (name) link.setAttribute('download', name)

    link.click()
    link.remove()
  }

  const recordingUrl = (camera, index, onProgress) => {
    if (camera.recording) {
      onProgress(index, 100)

      return Promise.resolve(camera.recording.url)
    }

    return createJob(camera).then(id => listenJob(id, percent => onProgress(index, percent)))
  }

  const run = (key, items, stored, title) => {
    if (downloading.value || !items.length) return

    if (stored) {
      downloadFile(stored.url, stored.name)
      return
    }

    downloading.value = key

    const progress = items.map(() => 0)

    const notification = {
      id: `recording-request-download-${idRecording}-${key}`,
      progress: 0,
      completed: false,
      view: 'show_recording',
      url: '',
      title,
      fileName: title,
      download: title,
      body: getProgressMessage(),
      error: false,
      timestamp: notificationTimestamp(),
    }

    setNotification(notification, 'grid/addDownloadNotification')

    const onProgress = (index, percent) => {
      progress.splice(index, 1, percent)

      const total = Math.round(progress.reduce((sum, value) => sum + value, 0) / progress.length)

      setNotification({ ...notification, progress: total, body: getProgressMessage(total) })
    }

    Promise.allSettled(items.map((camera, index) => recordingUrl(camera, index, onProgress)))
      .then(results => {
        const done = []
        const failed = []

        results.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            done.push({ camera: items[index], url: result.value })
            return
          }

          failed.push(items[index])
        })

        if (!done.length) return Promise.reject(new Error('empty'))

        setNotification({ ...notification, progress: 90, body: getProgressMessage(90) })

        return storeJob(done.map(item => item.camera), done.map(item => item.url))
          .then(id => listenJob(id, () => {}))
          .then(url => ({ url, failed }))
      })
      .then(({ url, failed }) => {
        setNotification({
          ...notification,
          progress: 100,
          completed: true,
          url,
          body: failed.length
            ? partialMessage(items.length - failed.length, items.length)
            : getProgressMessage(100),
        })

        if (failed.length) {
          toast({
            component: ToastificationContent,
            props: {
              title: `No se pudo descargar: ${failed.map(item => item.camera.name).join(', ')}`,
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
        }

        return fetchRecording()
      })
      .catch(() => {
        setNotification({
          ...notification,
          progress: 100,
          completed: true,
          body: 'Ocurrio un error',
          error: true,
        })

        toast({
          component: ToastificationContent,
          props: {
            title: 'Ocurrio un error al descargar las grabaciones',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .finally(() => {
        downloading.value = null
      })
  }

  const download = camera => run(
    camera.id,
    [camera],
    camera.recording,
    fileNameFormat(`export ${camera.camera.name} ${camera.from}`)
  )

  const downloadAll = () => run(
    'all',
    cameras.value,
    recordingOptions.value?.recordings,
    `RR-${String(idRecording).padStart(6, '0')}`
  )

  const fetchRecording = () => store.dispatch('recording_request/get', idRecording)
        .then(response => {
          recordingOptions.value = response.data
        })

  fetchRecording()

  toast({
    component: ToastificationContent,
      props: {
        title: "Buscando grabaciones",
        icon: "CheckIcon",
        variant: "success",
      },
  })

  return {
    recordingOptions,
    cameras,
    isApproved,
    idRecording,
    downloading,
    formatDateAssigned,
    download,
    downloadAll
  }
}
